// Sanctum API client. Default mode is **cookie-based session auth**
// (audit C7). The browser SPA sends `credentials: 'include'` and Sanctum
// reads the encrypted session cookie + CSRF cookie on every request. A
// successful XSS therefore can't read the auth material — cookies are
// httpOnly and not exposed to JavaScript.
//
// Bearer tokens are still supported as a back-compat fallback (for any
// long-lived localStorage token from before the migration, and for
// callers like the in-page Reverb echo client that fetch on raw fetch
// builds). New SPA logins do NOT write to tokenStore — the session
// cookie is the auth material from there on.

const BASE = import.meta.env.VITE_API_BASE_URL || "/api";

/**
 * Absolute URL for a public API path — for plain `<a href>` links (e.g. the
 * Tracker app download) that aren't authenticated XHRs. Mirrors the url
 * building in api()/downloadBlob() but without auth headers.
 */
export function apiUrl(path: string): string {
  if (path.startsWith("http")) return path;
  return `${BASE}${path.startsWith("/") ? path : `/${path}`}`;
}
const TOKEN_KEY = "control.token";
const TENANT_KEY = "control.tenant_id";
/** localStorage key of the active tenant (the X-Tenant-Id every call sends). */
export const TENANT_STORAGE_KEY = TENANT_KEY;
const XSRF_COOKIE = "XSRF-TOKEN";

/**
 * Fetch the CSRF cookie from Sanctum. Called once on app boot and again
 * after any 419 (CSRF mismatch) response. Sanctum responds 204 with the
 * `XSRF-TOKEN` and `<app>_session` cookies set; subsequent requests
 * include them automatically because of credentials: 'include'.
 */
let csrfPromise: Promise<void> | null = null;
let csrfReady = false;
/**
 * Fetch the CSRF cookie. Cached per-tab — once we have it, we don't
 * re-fetch unless `{force: true}` is passed (used after a 419 to
 * recover from a stale session) or the cookie is genuinely missing.
 */
export function ensureCsrfCookie(opts: { force?: boolean } = {}): Promise<void> {
  // Re-fetch when forced, or when a finished fetch's cookie has since gone
  // missing (storage cleared). The old check only ran when there was no
  // promise, so it never did anything (audit FE-30). Waiting on csrfReady
  // stops concurrent first calls each starting their own fetch.
  if (opts.force || (csrfReady && readCookie(XSRF_COOKIE) == null)) {
    csrfPromise = null;
    csrfReady = false;
  }
  if (!csrfPromise) {
    csrfPromise = fetch("/sanctum/csrf-cookie", {
      method: "GET",
      credentials: "include",
    }).then(() => { csrfReady = true; }).catch(() => {
      csrfPromise = null;
    });
  }
  return csrfPromise;
}

/** The X-XSRF-TOKEN header for callers that build their own fetch (portal). */
export function xsrfHeader(): Record<string, string> {
  const xsrf = readCookie(XSRF_COOKIE);
  return xsrf ? { "X-XSRF-TOKEN": xsrf } : {};
}

function readCookie(name: string): string | null {
  const matches = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return matches ? decodeURIComponent(matches[1]) : null;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  code?: string;
  constructor(status: number, body: unknown, message: string, code?: string) {
    super(message);
    this.status = status;
    this.body = body;
    this.code = code;
  }
}

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t: string | null) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY)),
};

export const tenantStore = {
  get: () => {
    const v = localStorage.getItem(TENANT_KEY);
    return v ? Number(v) : null;
  },
  set: (id: number | null) => (id ? localStorage.setItem(TENANT_KEY, String(id)) : localStorage.removeItem(TENANT_KEY)),
};

export interface RequestOptions extends Omit<RequestInit, "body" | "headers"> {
  body?: unknown;
  headers?: Record<string, string>;
  /** When true, omit Authorization header (for /auth/login etc.). */
  anon?: boolean;
  /**
   * When true, suppress the `data:changed` bus event after this call.
   * Use for read-only POSTs (server-side rendering of previews,
   * test-connection probes, etc.) that shouldn't make every list on
   * the page re-fetch.
   */
  silent?: boolean;
  /** Internal — set on the second call when api() retries after a 419. */
  _retry?: boolean;
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = path.startsWith("http") ? path : `${BASE}${path.startsWith("/") ? path : `/${path}`}`;

  // FormData must NOT have a Content-Type header set explicitly — fetch
  // computes the boundary string automatically. Setting application/json
  // here would break multipart uploads server-side.
  const isFormData = typeof FormData !== "undefined" && opts.body instanceof FormData;
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.body !== undefined && !isFormData ? { "Content-Type": "application/json" } : {}),
    ...(opts.headers ?? {}),
  };

  if (!opts.anon) {
    // Prefer cookie auth (audit C7). Bearer is still attached for any
    // call that runs before the cookie is established (e.g. legacy
    // localStorage token from a pre-C7 session) — Sanctum's guard
    // chain accepts both, the cookie wins when present.
    const token = tokenStore.get();
    if (token) headers.Authorization = `Bearer ${token}`;
    // An explicit X-Tenant-Id wins — the offline outbox replays each
    // write under the tenant it was queued in, not the current one.
    const tid = tenantStore.get();
    if (tid && !headers["X-Tenant-Id"]) headers["X-Tenant-Id"] = String(tid);
  }
  // CSRF: ALWAYS attach the X-XSRF-TOKEN header (even on `anon: true`
  // requests like /v1/auth/login). CSRF is request-integrity, not
  // authentication — Sanctum's stateful middleware kicks in for any
  // request originating from a SANCTUM_STATEFUL_DOMAINS host, including
  // the login endpoint, and rejects requests without a matching token
  // as 419. Pre-fix this code only set the header when `!anon`, which
  // worked when SPA + API were on different origins (Sanctum skipped
  // stateful entirely) but broke the moment everything moved behind
  // Traefik on a single origin.
  const xsrf = readCookie(XSRF_COOKIE);
  if (xsrf) headers["X-XSRF-TOKEN"] = xsrf;

  const res = await fetch(url, {
    ...opts,
    headers,
    // credentials: 'include' is the load-bearing change. With it, the
    // browser ships the session cookie + XSRF cookie automatically.
    // Without it, Sanctum SPA mode never receives the session.
    credentials: "include",
    body: opts.body !== undefined ? (opts.body instanceof FormData ? opts.body : JSON.stringify(opts.body)) : undefined,
  });

  // 419 = CSRF token mismatch. Refresh the cookie + retry once. We
  // do this for ALL requests (including `anon: true` ones like login)
  // — CSRF is request-integrity, not authentication, and a stale
  // XSRF-TOKEN from an expired session is a common cause of login
  // failing with 419 on the very first attempt after the user lets
  // the tab sit idle.
  if (res.status === 419 && !opts._retry) {
    await ensureCsrfCookie({ force: true });
    return api<T>(path, { ...opts, _retry: true });
  }

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }

  if (!res.ok) {
    const body = data as Record<string, unknown> | null;
    const message =
      (body && typeof body === "object" && typeof body.message === "string" && body.message) ||
      `${res.status} ${res.statusText}`;
    const code = body && typeof body === "object" && typeof body.code === "string" ? body.code : undefined;

    // 401 on an authed request = the cookie/token died (idle session
    // expiry, server-side revoke, etc.). Clear local auth state and
    // fan out `auth:expired` so the AuthProvider can drop the cached
    // user and let RequireAuth bounce the next render to /auth/login.
    // Anon requests (login itself, public KB lookup) and any path that
    // already returns 401 as a normal control-flow value (the 2fa
    // challenge endpoint) are excluded so we don't kick the user out
    // of a half-completed flow.
    if (res.status === 401 && !opts.anon && code !== "two_factor_required") {
      tokenStore.set(null);
      tenantStore.set(null);
      void import("./bus").then((m) => m.bus.emit("auth:expired", { url }))
        .catch(() => { /* tree-shaken in tests */ });
    }

    if (res.status === 402) {
      void import("./bus").then((m) => m.bus.emit("billing:required", { message, code }))
        .catch(() => { /* tree-shaken in tests */ });
    }

    throw new ApiError(res.status, data, message, code);
  }

  // Notify the in-process bus so other screens (dropdowns, sibling
  // lists) know to re-fetch. Cheap when nobody's listening; very
  // useful when a modal-driven create needs to refresh a parent view.
  // Dynamic import keeps this module free of bus dependency cycles.
  if (!opts.silent && opts.method && ["POST", "PATCH", "PUT", "DELETE"].includes(String(opts.method).toUpperCase())) {
    void import("./bus").then((m) => m.bus.emit("data:changed", { url, method: opts.method }))
      .catch(() => { /* tree-shaken in tests */ });
  }

  return data as T;
}

/**
 * Authenticated file download. Top-level browser navigation (a plain
 * `<a href="/api/...">`) can't carry the bearer token from localStorage,
 * and even with sanctum stateful cookies it depends on a freshly minted
 * session that may not be present. Fetching as a blob with the same
 * auth + CSRF plumbing as `api()` and handing the result to a temporary
 * anchor avoids the entire "Route [login] not defined" failure mode.
 */
export async function downloadBlob(path: string, fallbackFilename: string): Promise<void> {
  const url = path.startsWith("http") ? path : `${BASE}${path.startsWith("/") ? path : `/${path}`}`;
  const headers: Record<string, string> = {
    Accept: "application/octet-stream, application/pdf, */*",
  };
  const token = tokenStore.get();
  if (token) headers.Authorization = `Bearer ${token}`;
  const tid = tenantStore.get();
  if (tid) headers["X-Tenant-Id"] = String(tid);
  const xsrf = readCookie(XSRF_COOKIE);
  if (xsrf) headers["X-XSRF-TOKEN"] = xsrf;

  const res = await fetch(url, { headers, credentials: "include" });
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* not JSON */ }
    const message =
      (body && typeof body === "object" && body && "message" in body && typeof (body as { message?: unknown }).message === "string"
        ? (body as { message: string }).message
        : null) ?? `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, body, message);
  }

  const disposition = res.headers.get("content-disposition") ?? "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : fallbackFilename;

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revoke so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export const get  = <T = unknown>(path: string, opts?: RequestOptions) => api<T>(path, { ...opts, method: "GET" });
export const post = <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) => api<T>(path, { ...opts, method: "POST", body });
export const put  = <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) => api<T>(path, { ...opts, method: "PUT", body });
export const patch= <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) => api<T>(path, { ...opts, method: "PATCH", body });
export const del  = <T = unknown>(path: string, opts?: RequestOptions) => api<T>(path, { ...opts, method: "DELETE" });
